import { useMemo } from 'react';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import {
  getClientSecret,
  getClientSecretLink,
  GetLocationPaymentMethodsRequest,
  GetPersonPaymentMethodsRequest,
  PaymentMethod,
  PaymentPlanQueries,
  PersonPaymentMethodsResponse,
} from '@frontend/api-payment-plans';
import { isWeaveUser } from '@frontend/auth-helpers';
import { http } from '@frontend/fetch';

export type UseCardOnFileStripeApiProps = {
  isPublic: boolean;
};

export type CardOnFileStripeApi = {
  useGetPaymentMethodSetupLink: (
    request: GetLocationPaymentMethodsRequest,
    options?: UseQueryOptions<PersonPaymentMethodsResponse, unknown, string | undefined>
  ) => UseQueryResult<string, unknown>;
  getClientSecretLink: typeof getClientSecretLink;
  getClientSecret: typeof getClientSecret;
  isUserAuthenticated: () => boolean;
  useGetPaymentMethodsByPerson: (
    request: GetPersonPaymentMethodsRequest,
    options?: UseQueryOptions<PaymentMethod[], unknown, PaymentMethod[]>
  ) => UseQueryResult<PaymentMethod[], unknown>;
};

export const useCardOnFileStripeApi = ({ isPublic }: UseCardOnFileStripeApiProps) => {
  // Define the public implementation

  const getPublicImplementation = (): CardOnFileStripeApi => {
    return {
      useGetPaymentMethodSetupLink: () => {
        console.warn('unimplemented method');
        return {
          data: undefined,
          isLoading: false,
          isError: false,
          error: null,
        } as UseQueryResult<string, unknown>;
      },
      getClientSecretLink: (
        _setupLink: string | undefined,
        _data: {
          data: {
            personId?: string;
            locationId: string;
          };
        }
      ) => {
        console.warn('unimplemented method');
        return Promise.resolve('');
      },
      getClientSecret: (_secretLink: string | undefined, _locationId: string) => {
        console.warn('unimplemented method');
        return Promise.resolve(undefined);
      },
      isUserAuthenticated: () => false,
      useGetPaymentMethodsByPerson: () => {
        console.warn('unimplemented method');
        return {
          data: undefined,
          isLoading: false,
          isError: false,
          error: null,
        } as UseQueryResult<PaymentMethod[], unknown>;
      },
    };
  };

  // Define the authenticated implementation

  const getAuthenticatedImplementation = (): CardOnFileStripeApi => {
    const isUserAuthenticated = () => {
      /**
       * TODO: Potentially need public endpoint equivalent
       */

      return !(http.baseUrl.includes('api.weaveconnect.com') && isWeaveUser());
    };

    return {
      /**
       * TODO: Potentially need public endpoint equivalent
       */
      useGetPaymentMethodSetupLink: PaymentPlanQueries.useGetPaymentMethodSetupLink,

      /**
       * TODO: Potentially need public endpoint equivalent
       */
      getClientSecretLink,

      /**
       * TODO: Potentially need public endpoint equivalent
       */
      getClientSecret,

      /**
       * TODO: Potentially need public endpoint equivalent
       */
      isUserAuthenticated,

      useGetPaymentMethodsByPerson: PaymentPlanQueries.useGetPaymentMethodsByPerson,
    };
  };

  const apiImplementation = useMemo(() => {
    return isPublic ? getPublicImplementation() : getAuthenticatedImplementation();
  }, [isPublic]);

  // Return the appropriate implementation based on isPublic

  return apiImplementation;
};
